import "./insChartSettings.scss";
import React from "react";
import { InsSettingsChartType, InsSettingsSelectedValues, InsSettingsSortBy, InsSettingsDisplayPercent, InsSettingsGranularity, InsSettingsDisplayOptions, InsSettingsViewForm, InsSettingsScale, InsSettingsPeriodOfAnalysis } from "./chart-settings";
import { InsSettingsExcludeSoldAssets } from "./chart-settings/insSettingsExcludeSoldAssets";
import { InsSettingsTimePeriod } from "../../../time-period/insSettingsTimePeriod";
import { InsTenorSettings } from "./chart-settings/insTenorSettings";
import { useGetDatapointsMetadata } from "api/ux/src";
import { intersperse } from "common/array-helper";
import { WidgetFactoryComponentNames } from "components/widgets/base/insWidgetFactory";
import { useWidgetComponentName, usePatchedWidgetComponentProps, useWidgetComponentProps, useWidgetCurrentGridSwitch, useIsTimePeriodAvailable, useWidgetCurrentChartType, useIsTenorSettingsAvailable } from "components/widgets/hooks/use-current-widget";
import { isViewFormSettingAllowed } from "store/current-dashboard/utils";
export const useDatascale = (sectionId, widgetId) => {
    const [datapointsMetadata] = useGetDatapointsMetadata();
    let { datapointParams } = useWidgetComponentProps(sectionId, widgetId);
    const widgetComponentName = useWidgetComponentName(sectionId, widgetId);
    const { gridSwitcher } = usePatchedWidgetComponentProps(sectionId, widgetId), isGridOrValuePairComponent = widgetComponentName === WidgetFactoryComponentNames.insDatapointGridWidget ||
        widgetComponentName === WidgetFactoryComponentNames.insValuePairDatapointWidget, switched = useWidgetCurrentGridSwitch(sectionId, widgetId);
    datapointParams = switched && gridSwitcher?.datapointParams ? gridSwitcher?.datapointParams : datapointParams;
    const measureDatapointIds = datapointParams?.flatMap((param) => param.measures?.flatMap((measure) => [
        measure.standardDatapoint,
        measure.customDatapoint
    ].filter(Boolean)) || []);
    const datapointFormats = datapointsMetadata
        .filter((datapointMeta) => measureDatapointIds?.includes(datapointMeta.id))
        .map((n) => n.metadata?.format.toString());
    return isGridOrValuePairComponent && (datapointFormats.includes("amount") || datapointFormats.includes("unit"));
};
/* eslint-disable max-statements */
const buildSettings = (props, userSettings, options) => {
    const { sectionId, widgetId } = props;
    const { chartType, isDataScaleVisible, isGridWidget, isKpiWithoutHistory, isKpiWithoutVariation, isTenorSettingsAvailable, isTimePeriodAvailable } = options;
    const { chartTypeAvailable, shownValues, sortBy, displayValueType, viewForm, dataGranularity, displayOptions, periodOfAnalysis, excludeSoldAssets, dataScale } = userSettings || {};
    const settings = [];
    chartTypeAvailable && settings.push(React.createElement(InsSettingsChartType, { ...props }));
    isTimePeriodAvailable && !isKpiWithoutHistory && settings.push(React.createElement(InsSettingsTimePeriod, { widgetIds: [widgetId], sectionId: sectionId, isWidgetSettings: true }));
    // TEMPORARY: Hide tenor settings on grids
    isTenorSettingsAvailable && !isGridWidget && settings.push(React.createElement(InsTenorSettings, { ...props }));
    sortBy && settings.push(React.createElement(InsSettingsSortBy, { ...props }));
    displayOptions && settings.push(React.createElement(InsSettingsDisplayOptions, { ...props }));
    shownValues && settings.push(React.createElement(InsSettingsSelectedValues, { ...props }));
    displayValueType && settings.push(React.createElement(InsSettingsDisplayPercent, { ...props }));
    dataGranularity && !isKpiWithoutVariation && settings.push(React.createElement(InsSettingsGranularity, { ...props }));
    viewForm && isViewFormSettingAllowed(chartType) && settings.push(React.createElement(InsSettingsViewForm, { ...props }));
    dataScale !== undefined && isDataScaleVisible && settings.push(React.createElement(InsSettingsScale, { ...props }));
    periodOfAnalysis?.active && settings.push(React.createElement(InsSettingsPeriodOfAnalysis, { widgetId: widgetId, sectionId: sectionId }));
    excludeSoldAssets !== undefined && settings.push(React.createElement(InsSettingsExcludeSoldAssets, { ...props }));
    return intersperse(settings, React.createElement("hr", { className: "insChartSettings__divider" })).map((elem, index) => ({ ...elem, key: index }));
};
export const InsChartSettings = (props) => {
    const { sectionId, widgetId } = props;
    const componentProps = useWidgetComponentProps(sectionId, widgetId);
    const componentName = useWidgetComponentName(sectionId, widgetId);
    const { userSettings } = componentProps;
    const chartType = useWidgetCurrentChartType(sectionId, widgetId), isDataScaleVisible = useDatascale(sectionId, widgetId);
    const isTenorSettingsAvailable = useIsTenorSettingsAvailable(sectionId, widgetId);
    const isKpi = componentName === "InsKpiDatapointWidget";
    const kpiProps = componentProps;
    const isKpiWithoutHistory = isKpi && !kpiProps?.sparkline;
    const isKpiWithoutVariation = isKpi && !kpiProps?.variationOfMetric;
    // TEMPORARY: Check if this is a grid widget to hide tenor settings
    const isGridWidget = componentName === WidgetFactoryComponentNames.insDatapointGridWidget;
    const settings = buildSettings(props, userSettings, {
        chartType,
        isDataScaleVisible,
        isGridWidget,
        isKpiWithoutHistory,
        isKpiWithoutVariation,
        isTenorSettingsAvailable,
        isTimePeriodAvailable: useIsTimePeriodAvailable(sectionId, widgetId)
    });
    return React.createElement("div", { className: "insChartSettings" },
        React.createElement("div", { slot: "content", className: "settingContent" }, settings));
};
